<template>
  <div v-if="hasCoupons" class="discountBanner">
    <div :class="{discountProgress: true, active: isOpen}"
         @click="toggleOpen()"
         @mouseover="onHover(true)"
         @mouseleave="onHover(false)"
    >
      <svg class="discount" :style="{'left': `${discountPercentage}%`}" viewBox="0 0 14.96 28.67">
        <path class="accent--text background"
              d="M.9,0H14.06a.9.9,0,0,1,.9.9V19.46a3.19,3.19,0,0,1-.69,2L9.1,27.89a2.07,2.07,0,0,1-3.24,0L.69,21.43a3.13,3.13,0,0,1-.69-2V.9A.9.9,0,0,1,.9,0Z"/>
        <path class="foreground"
              d="M9.86,14.82A1.83,1.83,0,1,1,11.69,13,1.83,1.83,0,0,1,9.86,14.82Zm0-2.72a.9.9,0,1,0,.89.89A.89.89,0,0,0,9.86,12.1Z"/>
        <path class="foreground"
              d="M5.1,10.06A1.83,1.83,0,1,1,6.93,8.23,1.83,1.83,0,0,1,5.1,10.06Zm0-2.72A.9.9,0,1,0,6,8.23.89.89,0,0,0,5.1,7.34Z"/>
        <path class="foreground"
              d="M3.74,14.82a.47.47,0,0,1-.33-.8l7.48-7.48a.47.47,0,0,1,.66.66L4.07,14.68A.47.47,0,0,1,3.74,14.82Z"/>
      </svg>
      <div class="meter">
        <span class="secondary progress" :style="{width: `${discountPercentage}%`}"></span>
        <span v-for="coupon in coupons" :class="{marker: true, accent: couponActive(coupon)}"
              :style="{'left': calcMeterLeft(percentageOf(coupon.minimumValue))}"></span>
        <div class="secondary tooltip">
          <div v-if="expiryDate" class="couponContainer">
            <svg class="coupon" viewBox="0 0 256.3 73.19">
              <path class="secondary--text inheritFill"
                    d="M249.17,36.59a6.15,6.15,0,0,0,6.15,6.15,5.33,5.33,0,0,0,1-.09V73.19H0V42.74a6.15,6.15,0,0,0,6.15-6.15A6.15,6.15,0,0,0,0,30.45V0H256.3V30.54a5.33,5.33,0,0,0-1-.09A6.15,6.15,0,0,0,249.17,36.59Z"/>
              <path class="accent--text inheritFill"
                    d="M98.37,59.32a.5.5,0,0,1-.5-.5V14.22a.5.5,0,1,1,1,0v44.6A.5.5,0,0,1,98.37,59.32Z"/>
              <circle class="foreground" cx="54.26" cy="36.59" r="23.11"/>
              <path class="secondary--text inheritFill"
                    d="M60.84,48.21a5,5,0,1,1,5-5A5,5,0,0,1,60.84,48.21Zm0-7.51a2.47,2.47,0,1,0,2.46,2.47A2.48,2.48,0,0,0,60.84,40.7Z"/>
              <path class="secondary--text inheritFill"
                    d="M47.69,35.07A5.05,5.05,0,1,1,52.74,30,5.06,5.06,0,0,1,47.69,35.07Zm0-7.51A2.47,2.47,0,1,0,50.16,30,2.46,2.46,0,0,0,47.69,27.56Z"/>
              <path class="secondary--text inheritFill"
                    d="M43.93,48.21a1.28,1.28,0,0,1-.91-.37A1.3,1.3,0,0,1,43,46L63.68,25.35a1.29,1.29,0,0,1,1.83,1.83L44.85,47.84A1.28,1.28,0,0,1,43.93,48.21Z"/>
            </svg>
            <p class="validUntil">{{ $t("COUPON_VALID_UNTIL") }}</p>
            <p class="validUntilDate">{{ formatDate(expiryDate) }}</p>
          </div>
          <div class="steps">
            <h3 class="discountTitle">{{ $t("COUPON_TITLE") }}</h3>
            <div class="coupon" v-for="coupon in coupons">
              <v-divider :class="{secondary: couponActive(coupon)}"/>
              <div v-if="coupon === nextCoupon" class="missingNext">
                <p v-html="$t('COUPON_VALUE_MISSING').replace('{value}', formatPrice((coupon.minimumValue - subtotal)))"></p>
                <svg viewBox="0 0 285.2 246.99">
                  <polygon class="secondary--text caret" style="stroke-miterlimit: 10;"
                           points="142.6 245.99 0.87 0.5 284.33 0.5 142.6 245.99"/>
                </svg>
              </div>
              <div class="couponInfo">
                <div :class="{couponDiscount: true, secondary: couponActive(coupon)}">
                  <span v-if="coupon.type === 'VALUE'">-{{ coupon.discount }}€</span>
                  <span v-if="coupon.type === 'PERCENTAGE'">-{{ coupon.discount }}%</span>
                  <v-icon v-if="coupon.type === 'FREE_SHIPPING'" size="30px">local_shipping</v-icon>
                </div>
                <p class="condition"
                   v-html="$t('COUPON_CONDITION').replace('{value}', formatPrice(coupon.minimumValue))"></p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p v-if="hasNextCoupon" class="missing"
         v-html="$t('COUPON_MISSING_NEXT').replace('{value}', formatPrice(nextCoupon.minimumValue - subtotal))"></p>
      <p v-else class="missing">
        <v-icon size="16px">check</v-icon>
        {{ $t('COUPON_ALL_ACTIVATED') }}
      </p>
    </div>
  </div>

</template>

<script lang="ts">
import Component from 'vue-class-component'
import BasicComponent from "@/global/BasicComponent";
import {BasketDto, CouponDto} from "@swagger";

@Component({
  components: {}
})
export default class DiscountBanner extends BasicComponent {

  open: boolean = false;
  hover: boolean = false;

  get coupons(): Array<CouponDto> {
    return this.$store.getters.coupons;
  }

  get hasCoupons() {
    return this.coupons && this.coupons.length > 0;
  }

  get nextCouponIndex() {
    let i = 0;
    for (; i < this.coupons.length; i++) {
      if (this.coupons[i].minimumValue > this.subtotal) break;
    }
    return i;
  }

  get hasNextCoupon() {
    return this.nextCouponIndex < this.coupons.length;
  }

  get nextCoupon() {
    if (!this.hasNextCoupon) return null;
    return this.coupons[this.nextCouponIndex];
  }

  get maxGoal(): number {
    if (!this.hasCoupons) return 0.0;
    return this.coupons[this.coupons.length - 1].minimumValue;
  }

  get basket(): BasketDto {
    return this.$store.getters.basket;
  }

  get subtotal(): number {
    const basket = this.basket;
    if (!basket) return 0.0;
    return basket.subtotal;
  }

  couponActive(coupon: CouponDto): boolean {
    return this.subtotal >= coupon.minimumValue;
  }

  percentageOf(value: number): number {
    if (this.maxGoal === 0.0) return 0;
    return Math.min(100, value * 100 / this.maxGoal);
  }

  get discountPercentage(): number {
    return this.percentageOf(this.subtotal);
  }

  calcMeterLeft(perc: number): string {
    const offset = (50 - perc) * 15 / 50;
    return "calc(" + perc + "% + (" + offset + "px))";
  }

  get expiryDate(): string {
    const dates = this.coupons.map(coupon => coupon.activeUntil).filter(el => el != null).sort();
    if (dates.length === 0) return null;
    return dates[0];
  }

  toggleOpen() {
    if (this.hover) return;
    this.open = !this.open;
  }

  onHover(val) {
    if (val && !this.hover) {
      this.trackEvent("Gutschein", "Gutscheinliste geöffnet")
    }
    if (this.isMobile) return;
    this.hover = val;
  }

  get isOpen() {
    return this.open || this.hover;
  }

}

</script>
<style lang="scss" scoped>

.discountBanner {
  display: flex;
  align-items: center;
  margin: 0 auto;

  min-width: 100px;
  width: 350px;
  max-width: 100%;

  .discountIcon {
    height: 50px;
    margin-right: 20px;
  }

  @media screen and (max-width: 390px) {
    padding-left: 10px;
    padding-right: 10px;
  }

  .discountProgress {

    $discountHeight: 36px;

    cursor: pointer;
    position: relative;
    padding-top: $discountHeight;
    width: 100%;

    .discount {
      position: absolute;
      height: $discountHeight;
      z-index: 51;
      display: block;
      top: 0;
      transform: translateX(-50%);

      .foreground {
        fill: white;
      }

      .background {
        fill: currentColor;
      }
    }

    .progress {
      font-weight: 500;
      text-align: center;
      margin-bottom: 2px;
    }

    .missing {
      text-align: center;
      font-size: 14px;
      margin-top: 4px;

    }

    $meterHeight: 20px;

    .meter {
      height: $meterHeight;
      width: 100%;
      position: relative;
      background: #c9c9c9;
      -moz-border-radius: 25px;
      -webkit-border-radius: 25px;
      border-radius: 25px;
      box-shadow: inset 0 -1px 1px rgba(255, 255, 255, 0.3);

      .progress {
        display: block;
        height: 100%;
        border-radius: 20px;
        min-width: 20px;

        box-shadow: inset 0 2px 9px rgba(255, 255, 255, 0.3),
        inset 0 -2px 6px rgba(82, 82, 82, 0.4);
        position: relative;
        overflow: hidden;
        content: "";
      }

      .marker {
        height: $meterHeight;
        width: 2px;
        background-color: #3f3f3f;
        z-index: 50;
        display: block;
        position: absolute;
        top: 0;

        &.reached {
          background-color: var(--v-accent-base);
        }
      }

      .tooltip {
        $borderWidth: 3px;
        $pointerSize: 20px;

        visibility: hidden;
        width: 350px;
        max-width: 90vw;

        top: 50px;
        right: 50%;
        transform: translateX(50%);

        background-color: white !important;
        border: $borderWidth solid;
        color: black;
        padding: 5px;
        position: absolute;
        z-index: 50;

        &::before {
          content: "";
          position: absolute;
          border-style: solid;
          border-color: inherit;
          border-left-color: transparent;
          border-right-color: transparent;
          display: block;
          width: 0;
          top: calc(-#{$pointerSize});
          bottom: auto;
          left: auto;
          right: 50%;
          transform: translateX(50%);
          border-width: 0 $pointerSize $pointerSize;
        }

        &::after {
          content: "";
          position: absolute;
          border-style: solid;
          border-color: #fff transparent;
          display: block;
          width: 0;
          $offset: #{$pointerSize} - #{$borderWidth} - 2px;
          top: calc(0px - (#{$offset}));
          bottom: auto;
          right: 50%;
          transform: translateX(50%);
          border-width: 0 calc(#{$offset}) calc(#{$offset});
        }

        .couponContainer {
          position: relative;

          .coupon {
            width: 100%;

            .foreground {
              fill: white;
            }

            .inheritFill {
              fill: currentColor;
            }

          }

          p {
            text-align: left;
            position: absolute;
            left: 45%;

            &.validUntil {
              font-size: 110%;
              top: 20%;
              font-weight: 500;
            }

            &.validUntilDate {
              font-size: 200%;
              top: 40%;
            }
          }
        }

        .steps {

          padding-left: 5px;
          padding-right: 5px;

          .discountTitle {
            font-weight: 700;
            font-size: 30px;
            text-align: left;
            margin-top: 10px;
            margin-bottom: 10px;
            margin-left: 5px;
          }

          .v-divider {
            border-top-width: 1px;
          }

          .coupon {
            .missingNext {

              p {
                text-align: center;
                margin-top: 10px;
                margin-bottom: 10px;
              }

              svg {
                width: 14px;
                display: block;
                margin: 5px auto 0;

                .caret {
                  fill: currentColor;
                }
              }
            }

            .couponInfo {
              display: flex;
              padding-top: 10px;
              padding-bottom: 10px;
              align-items: center;

              .couponDiscount {
                $size: 30px;
                height: $size;
                width: $size;
                border: $size solid #aaaaaa;
                border-radius: $size;
                background-color: transparent !important;
                position: relative;

                * {
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                }

                span {
                  font-size: 16px;
                  font-weight: 500;
                }

                .v-icon {
                  color: white;
                  transform: translate(calc(-50% + 2px), -50%);
                }
              }

              .condition {
                margin: 0;
                margin-left: 15px;
                font-size: 16px;
              }
            }
          }
        }
      }
    }

    &.active {
      .tooltip {
        visibility: visible;
      }

      .missing {
        visibility: hidden;
      }
    }
  }
}

</style>
<style lang="scss">

</style>
